import { useEffect, useRef } from 'react';
import { useIdentity } from './useIdentity';
import { useMultiOrgCookies } from './useMultiOrgCookies';
import { isMultiOrgEnabled } from '../MultiOrg/utils';

/**
 * Genesis returns this when Zuora-Org-Ids contains orgs the current user cannot
 * access (typical after session-expiry login to a non-multi-org account).
 * fetchWrapper throws `new Error(body)`, so the JSON message is in error.message.
 */
export const isStaleOrgIdsAccessError = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
      ? error
      : '';
  return message.includes(
    "org zuora org ID(s) that was passed can't be accessed",
  );
};

/**
 * Clears Zuora-Org-Ids / Zuora-Working-Org when the current user does not have
 * MultiOrg permission. Needed because those cookies survive session expiry
 * (only cleared on explicit logout), so a later non-multi-org login would
 * otherwise keep sending stale org headers.
 *
 * Also clears when identity fails because genesis rejected those org ids —
 * useIdentity Promise.all fails even if /v1/identity succeeded, so the
 * permission-based path never runs without this fallback.
 */
export const useClearStaleMultiOrgCookies = (reloadPage?: () => void) => {
  const { data: identity, isError, error } = useIdentity();
  const { selectedOrgIds, clear } = useMultiOrgCookies();
  const hasCleared = useRef(false);

  useEffect(() => {
    if (hasCleared.current || !selectedOrgIds?.length) return;

    const staleOrgCookieBlockedIdentity =
      isError && isStaleOrgIdsAccessError(error);
    const nonMultiOrgUser = Boolean(identity && !isMultiOrgEnabled(identity));

    if (staleOrgCookieBlockedIdentity || nonMultiOrgUser) {
      hasCleared.current = true;
      clear();
      reloadPage?.();
    }
  }, [identity, isError, error, selectedOrgIds, clear, reloadPage]);
};

export const StaleMultiOrgCookiesCleaner = ({
  reloadPage,
}: {
  reloadPage?: () => void;
}) => {
  useClearStaleMultiOrgCookies(reloadPage);
  return null;
};
